import userAgent, { OperatingSystem, Browser, IPlatformParams } from 'app/utils/userAgent';
import Agent from 'app/utils/queues/Agent';

type NeededPlatformParams = Pick<IPlatformParams, 'os' | 'browser'>;

interface IState {
  loaded: boolean;
  requested: boolean;
}

/**
 * Unfortunately, the blessed way by Mozilla to make PDFJS use fake (main
 * thread) worker is to preload the worker code into the global scope. Ideally,
 * we'd make these two modules use their own fake "global" scope by wrapping
 * both with `Function` constructor and providing fake `window` object.
 *
 * It's important to load the worker code _before_ loading the PDFJS code so
 * that it discovers that it's present.
 *
 * https://github.com/mozilla/pdf.js/commit/56a8c934dde6cce806b1d74fa80c181eb30bbb96
 */
export class MainThreadWorkerLoader extends Agent<IState> {
  public constructor() {
    super({ init: { loaded: false, requested: false } });
  }

  /**
   * Load worker to main thread for browsers that are problematic/slow with
   * transferring data between workers and main thread.
   *
   * Make sure you `await` on this call. It'll reject if loading the code didn't
   * succeed.
   *
   * @returns Whether or not the loader is on the main thread.
   */
  public async loadMainThreadWorkerInProblematicBrowsers(ua?: NeededPlatformParams): Promise<boolean> {
    const { loaded } = await this.update(async (state) => {
      if (state.requested) {
        return state;
      }

      if (shouldUseMainThreadWorker(ua)) {
        await import('app/services/pdf/pdfjs.worker');
        return { loaded: true, requested: true };
      }

      return { loaded: false, requested: true };
    });

    return loaded;
  }
}

/**
 * Don't use workers in Safari and all iOS browsers (since they share the
 * engine) because of postMessage being super slow in some circumstances:
 * https://github.com/mozilla/pdf.js/issues/7431
 */
const shouldUseMainThreadWorker = ({ os, browser }: NeededPlatformParams = userAgent()) =>
  os === OperatingSystem.iOS || browser === Browser.Safari;

// Global instance. Making it context based would be a lie since it loads PDFJS
// code to global scope
export const MainThreadWorkerLoaderGlobalInstance = new MainThreadWorkerLoader();
